import { ORG_ROLE_KEYS, ORG_ROLE_PERMISSIONS, type OrgRoleKey } from "./roles";
import {
	ALL_PERMISSIONS,
	flattenPermissionSet,
	isPermission,
	type Permission,
	type PermissionResource,
} from "./statements";

/**
 * Org-configurable roles (Agent OS P2b, D30). Built-in roles keep their SkillQuest defaults in code;
 * an org may override a built-in role's permissions or define custom roles (`OrganizationRole` rows).
 *
 * Resources better-auth still enforces with its static roles (`organization`, `team`, `ac`) are
 * locked: always the built-in default, never editable, never part of a custom role (until P34 routes
 * those endpoints through `authorize`).
 */
export const LOCKED_PERMISSION_RESOURCES = [
	"organization",
	"team",
	"ac",
] as const satisfies readonly PermissionResource[];

/**
 * Member baseline (analyse-platform P1d, agreed with Agent OS): every role in every org holds these,
 * built-in or custom; the role editor shows them locked. `analytics:record_own` lets a member report
 * their own discovery interactions (impressions, opens, search sessions) so org analytics cover
 * everyone, whatever roles the org defines; `profile:edit_own` lets them fill their own member fields
 * (onboarding, self-edit) where the org allows it; `profile:read_fields` lets them see which member
 * fields the org defines (definitions, never values) — the audience builder needs it for any role
 * (create-orchestration D50).
 */
export const MEMBER_BASELINE_PERMISSIONS = [
	"analytics:record_own",
	"profile:edit_own",
	"profile:read_fields",
] as const satisfies readonly Permission[];

export function isMemberBaselinePermission(permission: Permission): boolean {
	return (MEMBER_BASELINE_PERMISSIONS as readonly Permission[]).includes(
		permission,
	);
}

/** Locked: a better-auth static resource or the member baseline — never editable on any role. */
export function isLockedPermission(permission: Permission): boolean {
	const resource = permission.split(":")[0];
	return (
		(LOCKED_PERMISSION_RESOURCES as readonly string[]).includes(
			resource ?? "",
		) || isMemberBaselinePermission(permission)
	);
}

export const EDITABLE_PERMISSIONS: readonly Permission[] =
	ALL_PERMISSIONS.filter((permission) => !isLockedPermission(permission));

/** The owner role is immutable: it always holds every permission. */
export const IMMUTABLE_ROLE_KEYS: readonly OrgRoleKey[] = ["owner"];

export function isBuiltInRoleKey(key: string): key is OrgRoleKey {
	return (ORG_ROLE_KEYS as readonly string[]).includes(key);
}

/** Custom role keys: lowercase slug, never a built-in key; stored in `member.role` (comma-separated). */
export const CUSTOM_ROLE_KEY_PATTERN = /^[a-z][a-z0-9_]{1,39}$/;

export function isValidCustomRoleKey(key: string): boolean {
	return CUSTOM_ROLE_KEY_PATTERN.test(key) && !isBuiltInRoleKey(key);
}

/** An `OrganizationRole` row as `authorize` needs it. */
export interface OrgRoleRecord {
	key: string;
	builtIn: boolean;
	permissions: unknown;
}

/** Valid, editable permissions from a stored `permissions` JSON value; anything else is dropped. */
export function parseStoredPermissions(value: unknown): Permission[] {
	if (!Array.isArray(value)) {
		return [];
	}
	return value.filter(
		(entry): entry is Permission =>
			typeof entry === "string" &&
			isPermission(entry) &&
			!isLockedPermission(entry),
	);
}

/** A built-in role's code defaults, always including the member baseline. */
export function defaultRolePermissions(key: OrgRoleKey): Permission[] {
	return withMemberBaseline(flattenPermissionSet(ORG_ROLE_PERMISSIONS[key]));
}

/** Every role key in a member's `role` column (built-in and custom), e.g. `"admin,reviewer_lead"`. */
export function parseRoleKeys(role: string | null | undefined): string[] {
	if (!role) {
		return [];
	}
	return role
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean);
}

/**
 * Permissions one role key grants in an org: a built-in role's override (editable part) plus its
 * locked defaults, else its code defaults; a custom role's stored permissions plus the `member`
 * role's locked defaults (e.g. `ac:read`, so holders can see roles); nothing for an unknown key
 * (e.g. a deleted custom role).
 */
export function resolveRoleKeyPermissions(
	key: string,
	orgRoles: readonly OrgRoleRecord[],
): Permission[] {
	const row = orgRoles.find((candidate) => candidate.key === key);
	if (isBuiltInRoleKey(key)) {
		const defaults = defaultRolePermissions(key);
		if (!row?.builtIn || IMMUTABLE_ROLE_KEYS.includes(key)) {
			return withMemberBaseline(defaults);
		}
		return withMemberBaseline([
			...defaults.filter(isLockedPermission),
			...parseStoredPermissions(row.permissions),
		]);
	}
	if (!row || row.builtIn) {
		return [];
	}
	return withMemberBaseline([
		...defaultRolePermissions("member").filter(isLockedPermission),
		...parseStoredPermissions(row.permissions),
	]);
}

function withMemberBaseline(permissions: Permission[]): Permission[] {
	return [...new Set([...permissions, ...MEMBER_BASELINE_PERMISSIONS])];
}
